import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { scrollBehaviorReducedAware } from '../../../lib/prefersReducedMotion';

const SCROLL_KEYS = new Set([
  ' ',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'ArrowUp',
  'ArrowDown',
]);

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Locks window scroll while a modal tour is open; restores position on release. */
export function usePageScrollLock(active: boolean, scrollableRoot?: RefObject<HTMLElement | null>) {
  const scrollYRef = useRef(0);
  const lockedRef = useRef(false);

  const lockScroll = useCallback(() => {
    if (lockedRef.current) return;
    scrollYRef.current = window.scrollY;
    lockedRef.current = true;
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.dataset.employerTourScrollLock = 'true';
  }, []);

  const unlockScroll = useCallback(() => {
    if (!lockedRef.current) return;
    const y = scrollYRef.current;
    document.documentElement.style.overflow = '';
    document.documentElement.style.overscrollBehavior = '';
    document.body.style.overflow = '';
    document.body.style.overscrollBehavior = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    delete document.body.dataset.employerTourScrollLock;
    lockedRef.current = false;
    window.scrollTo(0, y);
  }, []);

  /** Unlock, scroll target into view, then re-lock at the new position. */
  const scrollToAndLock = useCallback(
    (el: HTMLElement | null, onDone: () => void, behavior: ScrollBehavior = scrollBehaviorReducedAware()) => {
      unlockScroll();
      el?.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
      const delay = behavior === 'smooth' ? 420 : 64;
      return window.setTimeout(() => {
        scrollYRef.current = window.scrollY;
        lockScroll();
        onDone();
      }, delay);
    },
    [lockScroll, unlockScroll],
  );

  useEffect(() => {
    if (!active) return;

    const allowInsidePopover = (target: EventTarget | null) =>
      target instanceof Node && scrollableRoot?.current?.contains(target);

    const blockScroll = (event: Event) => {
      if (allowInsidePopover(event.target)) return;
      event.preventDefault();
    };

    const blockKeys = (event: KeyboardEvent) => {
      if (!SCROLL_KEYS.has(event.key)) return;
      if (allowInsidePopover(event.target)) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
    };

    window.addEventListener('wheel', blockScroll, { passive: false, capture: true });
    window.addEventListener('touchmove', blockScroll, { passive: false, capture: true });
    window.addEventListener('keydown', blockKeys, { capture: true });

    return () => {
      window.removeEventListener('wheel', blockScroll, true);
      window.removeEventListener('touchmove', blockScroll, true);
      window.removeEventListener('keydown', blockKeys, true);
      unlockScroll();
    };
  }, [active, scrollableRoot, unlockScroll]);

  return { lockScroll, unlockScroll, scrollToAndLock, isLocked: () => lockedRef.current };
}
